import { CanActivate, UrlTree } from '@angular/router';
import { UserAuthService } from '../user-auth/user-auth.service';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { GraphInitService } from '../graph/graph-init.service';
import { GraphService } from '../graph/graph.service';

@Injectable({ providedIn: 'root' })
export class DashboardAuthGuard implements CanActivate {
    constructor(private graphService: GraphService, private userAuthService: UserAuthService, private router: Router, private graphInitService: GraphInitService) { }
    async canActivate(): Promise<boolean | UrlTree> {
        if (!this.userAuthService.checkMsalAuth()) {
            return true;
        }
        // checkUserExisting() returns an Observable (it may trigger an async token
        // refresh), so it must be subscribed rather than used as a truthy value.
        // If the session is not valid we simply show the login page instead of
        // entering the authenticated branch with an expired token, which used to
        // hang on the Graph init calls and leave the user on a blank screen.
        return new Promise<boolean | UrlTree>((resolve) => {
            this.userAuthService.checkUserExisting().subscribe(
                async (isValid) => {
                    if (!isValid) {
                        resolve(true);
                        return;
                    }
                    try {
                        await this.ensureCommunicationSite();
                        await this.ensureSharePointHost();
                        resolve(this.router.createUrlTree(['dashboard']));
                    } catch (error) {
                        console.error('Dashboard guard initialization failed:', error);
                        resolve(true);
                    }
                },
                (error) => {
                    console.error('Auth check failed:', error);
                    resolve(true);
                }
            );
        });
    }

    /**
     * Restore the BluePrint communication site id from the cache. If the cached
     * id is invalid, corrupted or stale, discard it and rebuild from the server
     * instead of letting the error bubble up into a blank screen.
     */
    private async ensureCommunicationSite(): Promise<void> {
        const cachedId = localStorage.getItem('bluePrintCommunicationGroupId');
        if (cachedId != null) {
            this.graphService.bluePrintCommunicationGroupId = cachedId;
            return;
        }
        await this.graphInitService.createCommonCommunicationSite();
    }

    /**
     * Restore the SharePoint host details from the cache. If the cached value is
     * missing required fields or is not valid JSON (corrupted), discard it and
     * re-fetch it from the server.
     */
    private async ensureSharePointHost(): Promise<void> {
        const raw = localStorage.getItem('sharePointHost');
        if (raw != null) {
            try {
                const userDetails = JSON.parse(raw);
                if (userDetails && userDetails.userDomain && userDetails.sharePointHost) {
                    this.graphService.userDomain = userDetails.userDomain;
                    this.graphService.userDetails = userDetails.userDetails;
                    this.graphService.sharePointHost = userDetails.sharePointHost;
                    return;
                }
            } catch (error) {
                console.warn('Cached sharePointHost is corrupted - re-fetching:', error);
            }
            localStorage.removeItem('sharePointHost');
        }
        await this.graphInitService.getSharePointHostDetails();
    }
}
